import { z } from 'zod';

export const entityTypes = ['lead', 'contact', 'company', 'deal'] as const;
export type EntityType = (typeof entityTypes)[number];

const fieldCodeSchema = z.string().regex(/^[A-Z0-9_]+$/i).max(128);

export const entitySettingsSchema = z.object({
  fields: z.array(fieldCodeSchema).max(100).default([]),
  sort: z.array(fieldCodeSchema).max(100).default([])
});

export const callCardSettingsSchema = z.object({
  version: z.number().int().positive().default(2),
  __version: z.number().int().positive().default(2),
  lead: entitySettingsSchema.default({ fields: [], sort: [] }),
  contact: entitySettingsSchema.default({ fields: [], sort: [] }),
  company: entitySettingsSchema.default({ fields: [], sort: [] }),
  deal: entitySettingsSchema.default({ fields: [], sort: [] }),
  __advanced: z.object({
    merge_entities: z.boolean().default(true),
    rules: z.object({
      incoming_only: z.boolean().default(false),
      outgoing_only: z.boolean().default(false),
      worktime_only: z.boolean().default(false),
      weekdays_only: z.boolean().default(false)
    }).default({
      incoming_only: false,
      outgoing_only: false,
      worktime_only: false,
      weekdays_only: false
    })
  }).default({
    merge_entities: true,
    rules: {
      incoming_only: false,
      outgoing_only: false,
      worktime_only: false,
      weekdays_only: false
    }
  }),
  __meta: z.object({
    member_id: z.string().max(128).default(''),
    app_sid: z.string().max(256).default('')
  }).default({ member_id: '', app_sid: '' })
});

export type CallCardSettings = z.infer<typeof callCardSettingsSchema>;

export function defaultSettings(): CallCardSettings {
  return normalizeSettings({
    lead: {
      fields: ['TITLE', 'NAME', 'LAST_NAME', 'PHONE', 'EMAIL', 'STATUS_ID', 'ASSIGNED_BY_ID'],
      sort: ['TITLE', 'NAME', 'LAST_NAME', 'PHONE', 'EMAIL', 'STATUS_ID', 'ASSIGNED_BY_ID']
    },
    contact: {
      fields: ['NAME', 'LAST_NAME', 'PHONE', 'EMAIL', 'COMPANY_ID', 'ASSIGNED_BY_ID'],
      sort: ['NAME', 'LAST_NAME', 'PHONE', 'EMAIL', 'COMPANY_ID', 'ASSIGNED_BY_ID']
    },
    company: {
      fields: ['TITLE', 'PHONE', 'EMAIL', 'ADDRESS', 'ASSIGNED_BY_ID'],
      sort: ['TITLE', 'PHONE', 'EMAIL', 'ADDRESS', 'ASSIGNED_BY_ID']
    },
    deal: {
      fields: ['TITLE', 'STAGE_ID', 'OPPORTUNITY', 'CURRENCY_ID', 'ASSIGNED_BY_ID'],
      sort: ['TITLE', 'STAGE_ID', 'OPPORTUNITY', 'CURRENCY_ID', 'ASSIGNED_BY_ID']
    }
  });
}

export function normalizeSettings(input: unknown): CallCardSettings {
  const parsed = callCardSettingsSchema.parse(input);

  for (const type of entityTypes) {
    const selected = [...new Set(parsed[type].fields)];
    const order = [...new Set(parsed[type].sort.filter((code) => selected.includes(code)))];
    parsed[type] = {
      fields: selected,
      sort: [...order, ...selected.filter((code) => !order.includes(code))]
    };
  }

  parsed.version = 2;
  parsed.__version = 2;
  return parsed;
}
